import {
  chatPresentation,
  ChatItem,
  describeAnswer,
  expireOpenRows,
  isMultiSelect,
  liveQuestion,
  needsOwnConfirmation,
  QuestionDefinition,
  quickReplies,
  resolveRows,
  type ChatItemDraft,
  type ChatItemInput,
  type ConfirmationCard,
} from '@cyberscore/contracts';
import { KINTA_QUESTIONS } from '@cyberscore/contracts/fixtures';

/**
 * A scripted conversation for the chat design gallery. The rules (what is live, what needs confirming,
 * expiry) are the real ones from @cyberscore/contracts; only the interpreter is a stand-in for the
 * conversation agent (task 26), so the gallery never calls a model or saves an answer.
 */

export const DEMO_ORDER = ['Q021', 'Q012', 'Q058', 'Q330', 'Q104', 'Q106', 'Q402'] as const;
export const DEMO_QUESTIONS: Record<string, QuestionDefinition> = Object.fromEntries(
  DEMO_ORDER.map((k) => [k, QuestionDefinition.parse(KINTA_QUESTIONS[k])]),
);

const SAY: Record<string, [text: string, why: string]> = {
  Q021: ['Which email system does the team use?', 'It decides which connections we can offer you.'],
  Q012: [
    'Roughly how many people work for you, including part-timers?',
    'Size changes which controls apply.',
  ],
  Q058: ['When people sign in to email, do they also need a code or a prompt on their phone?', 'Stolen passwords are the most common way in.'], // prettier-ignore
  Q330: ['Which systems would stop production or sales if they were down for a day?', 'These are what a ransomware attack would hurt most.'], // prettier-ignore
  Q104: ['Is the business certified to ISO 27001?', 'A current certificate lets us skip some questions.'],
  Q106: ['When does the certificate expire?', 'We will remind you before it runs out.'],
  Q402: ['Do you have your insurance policy schedule to hand?', 'It shows what you are covered for today.'],
};
const STAGE_1 = ['Q021', 'Q012'];

export interface DemoState {
  items: ChatItem[];
  answers: Record<string, unknown>;
  skipped: string[];
  seq: number;
}

export type DemoAction =
  | { type: 'answer'; question: string; value: unknown; text: string; via: 'tapped' | 'form' }
  | { type: 'send'; text: string }
  | { type: 'confirm'; card: string; questions: string[] }
  | { type: 'decline'; card: string; questions: string[] }
  | { type: 'skip'; question: string }
  | { type: 'reset' };

export function startDemo(): DemoState {
  const s: DemoState = { items: [], answers: {}, skipped: [], seq: 0 };
  push(s, {
    kind: 'bot',
    text: 'Hi Aisyah. I’ll ask a few questions, one at a time. Tap an answer, or tell me in your own words and I’ll check I’ve understood before saving anything.',
  });
  return ask(s, DEMO_ORDER[0]);
}

export function demoReducer(prev: DemoState, action: DemoAction): DemoState {
  if (action.type === 'reset') return startDemo();
  const s: DemoState = {
    ...prev,
    items: [...prev.items],
    answers: { ...prev.answers },
    skipped: [...prev.skipped],
  };

  switch (action.type) {
    case 'answer': {
      s.items = expireOpenRows(s.items);
      push(s, { kind: 'user', text: action.text, via: action.via });
      s.answers[action.question] = action.value;
      return advance(s, 'Saved.');
    }
    case 'send': {
      s.items = expireOpenRows(s.items);
      push(s, { kind: 'user', text: action.text, via: 'typed' });
      const current = liveQuestion(s.items)?.question ?? nextKey(s);
      if (!current) {
        push(s, { kind: 'bot', text: 'That’s everything for now. You can still change an answer from the assessment page.' }); // prettier-ignore
        return s;
      }
      if (isHelp(action.text)) {
        push(s, { kind: 'bot', text: `${SAY[current]![1]} Tap an answer, or tell me in your own words.` });
        return s;
      }
      const turn = `turn-${s.seq}`;
      const rows = interpret(action.text, openKeys(s, current));
      if (!rows.length) {
        push(s, { kind: 'bot', text: 'I couldn’t match that to an answer. Which of these is closest?' });
        return ask(s, current, true);
      }
      push(s, { kind: 'confirmation', turn_id: turn, source: { kind: 'chat' }, rows });
      return s;
    }
    case 'confirm':
    case 'decline': {
      const i = s.items.findIndex((it) => it.id === action.card);
      const card = s.items[i] as ConfirmationCard | undefined;
      if (!card || card.kind !== 'confirmation') return prev;
      const open = new Set(card.rows.filter((r) => r.state === 'open').map((r) => r.question));
      const qs = action.questions.filter((q) => open.has(q));
      if (!qs.length) return prev;
      const resolved = resolveRows(card, qs, action.type === 'confirm' ? 'confirmed' : 'declined');
      s.items[i] = resolved;
      const stillOpen = resolved.rows.some((r) => r.state === 'open');
      if (action.type === 'confirm') {
        for (const r of card.rows) if (qs.includes(r.question)) s.answers[r.question] = r.value;
        const saved = qs.length > 1 ? `Saved ${qs.length} answers.` : 'Saved.';
        return stillOpen ? (push(s, { kind: 'bot', text: saved }), s) : advance(s, saved);
      }
      push(s, { kind: 'bot', text: 'No problem, nothing was saved.' });
      if (stillOpen) return s;
      const again = nextKey(s);
      return again ? ask(s, again, true) : s;
    }
    case 'skip': {
      s.items = expireOpenRows(s.items);
      push(s, { kind: 'user', text: 'Skip', via: 'tapped' });
      s.skipped.push(action.question);
      return advance(s, 'Skipped. You can come back to it from the assessment page.');
    }
  }
}

type NewRow = Extract<ChatItemInput, { kind: 'confirmation' }>['rows'][number];

function push(s: DemoState, item: ChatItemDraft) {
  s.seq += 1;
  s.items.push(ChatItem.parse({ ...item, id: `m${s.seq}` }));
}

function ask(s: DemoState, key: string, again = false): DemoState {
  const q = DEMO_QUESTIONS[key]!;
  const presentation = chatPresentation(q);
  push(s, {
    kind: 'question',
    question: key,
    stage: STAGE_1.includes(key) ? { key: 'S01', name: 'Know your business' } : null,
    text: SAY[key]![0],
    why: SAY[key]![1],
    presentation: presentation === 'not_asked' ? 'inline_form' : presentation,
    quick_replies: presentation === 'quick_replies' ? quickReplies(q) : [],
    multiple: isMultiSelect(q),
    kill_switch: q.metadata.kill_switch,
    optional: !q.mandatory,
    again,
  });
  return s;
}

/** Asked only after these answers, as the journey's depends_on and visibility would decide. */
const ASKED_IF: Record<string, (answers: Record<string, unknown>) => boolean> = {
  Q106: (a) => a.Q104 === 'yes',
};
const askable = (s: DemoState, k: string) =>
  !(k in s.answers) && !s.skipped.includes(k) && (ASKED_IF[k]?.(s.answers) ?? true);

const nextKey = (s: DemoState) => DEMO_ORDER.find((k) => askable(s, k)) ?? null;

/** The current question first, then the rest still open: one reply may answer several. */
const openKeys = (s: DemoState, current: string) => [
  current,
  ...DEMO_ORDER.filter((k) => k !== current && askable(s, k)),
];

function advance(s: DemoState, feedback: string): DemoState {
  push(s, { kind: 'bot', text: feedback });
  if (STAGE_1.every((k) => k in s.answers) && !s.items.some((it) => it.kind === 'stage'))
    push(s, { kind: 'stage', stage: 'S01', name: 'Know your business', message: 'Nice. We now know enough to tailor the rest of the check.' }); // prettier-ignore
  const next = nextKey(s);
  if (next) return ask(s, next);
  const required = DEMO_ORDER.filter((k) => DEMO_QUESTIONS[k]!.mandatory && !(k in s.answers)).length;
  push(s, { kind: 'done', answered: Object.keys(s.answers).length, required_left: required });
  return s;
}

const isHelp = (t: string) =>
  /\?\s*$/.test(t) || /^(why|what|how|which|can you|do i|does it)\b/i.test(t.trim());

type Rule = [question: string, pattern: RegExp, value: (m: RegExpMatchArray) => unknown, confidence: number, currentOnly?: boolean]; // prettier-ignore
const RULES: Rule[] = [
  ['Q021', /microsoft|office ?365|\bm365\b|outlook|\bteams\b/i, () => 'microsoft_365', 0.92],
  ['Q021', /google|gmail|workspace/i, () => 'google_workspace', 0.9],
  ['Q012', /\b(\d{1,5})\s*(people|staff|employees|workers|of us)\b/i, (m) => Number(m[1]), 0.9],
  ['Q012', /\b(\d{1,5})\b/, (m) => Number(m[1]), 0.8, true],
  ['Q058', /every(one|body)|all (of us|staff|users)/i, () => 'everyone', 0.88, true],
  ['Q058', /\b(some|most|a few|only (the )?admins?)\b/i, () => 'some', 0.8, true],
  ['Q058', /\b(no|nope)\b|just (a )?password|password only/i, () => 'no', 0.85, true],
  ['Q104', /\biso\b.*\b(no|not)\b|\b(no|not)\b.*\biso\b/i, () => 'no', 0.86],
  ['Q104', /\biso\b.*\b(yes|certified)\b|\bcertified\b.*\biso\b/i, () => 'yes', 0.84],
];

/**
 * Stand-in for the conversation agent: keyword rules over the open questions. Generic rules (a bare number,
 * "no") only answer the question being asked now. Values are checked like the agent's, before any card.
 */
export function interpret(text: string, open: readonly string[]) {
  const rows: NewRow[] = [];
  const quote = text.length <= 160 ? text : null;
  for (const key of open) {
    const q = DEMO_QUESTIONS[key]!;
    const rule = RULES.find(([k, re, , , cur]) => k === key && (!cur || key === open[0]) && re.test(text));
    if (!rule) continue;
    const value = rule[2](text.match(rule[1])!);
    rows.push({
      question: key,
      question_text: q.text,
      value,
      label: describeAnswer(q, value),
      confidence: rule[3],
      quote,
      own_confirmation: needsOwnConfirmation(q),
      kill_switch: q.metadata.kill_switch,
    });
  }
  return rows;
}
